/*
 *  Licensed under the EUPL, Version 1.2 or – as soon they will be approved by
the European Commission - subsequent versions of the EUPL (the "Licence");
You may not use this work except in compliance with the Licence.
You may obtain a copy of the Licence at:

  https://joinup.ec.europa.eu/software/page/eupl

Unless required by applicable law or agreed to in writing, software
distributed under the Licence is distributed on an "AS IS" basis,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the Licence for the specific language governing permissions and
limitations under the Licence. */

import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import React from "react";
import { useTheme } from "../ThemeProvider";

interface ThemeSwitcherProps {
  variant?: "standard" | "outlined" | "filled";
  size?: "small" | "medium";
  fullWidth?: boolean;
  label?: string;
}

/**
 * Theme Switcher Component
 *
 * Allows users to switch between different theme configurations at runtime.
 *
 * @example
 * ```tsx
 * import { ThemeSwitcher } from '../theme/components/ThemeSwitcher';
 *
 * function SettingsMenu() {
 *   return (
 *     <ThemeSwitcher
 *       variant="outlined"
 *       size="small"
 *       label="Select Theme"
 *     />
 *   );
 * }
 * ```
 */
export const ThemeSwitcher: React.FC<ThemeSwitcherProps> = ({
  variant = "outlined",
  size = "small",
  fullWidth = false,
  label = "Theme",
}) => {
  const { availableThemes, switchThemeConfig, currentThemePath } = useTheme();

  /**
   * Only the active theme's JSON is loaded, so the other options have no `name`
   * to show. The file name is therefore the label: `theme/entur-theme.json`
   * becomes "Entur Theme". Name theme files after the brand they represent.
   */
  const getThemeDisplayName = (themePath: string): string => {
    const fileName = themePath.split("/").pop()?.replace(".json", "") || "";

    return fileName
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleChange = async (event: SelectChangeEvent<string>) => {
    const newThemePath = event.target.value;
    await switchThemeConfig(newThemePath);
  };

  // Hide theme switcher if 0 or 1 themes available
  if (availableThemes.length <= 1) {
    return null;
  }

  return (
    <FormControl variant={variant} size={size} fullWidth={fullWidth}>
      <InputLabel id="theme-switcher-label">{label}</InputLabel>
      <Select
        labelId="theme-switcher-label"
        id="theme-switcher"
        value={currentThemePath}
        label={label}
        onChange={handleChange}
      >
        {availableThemes.map((themePath) => (
          <MenuItem key={themePath} value={themePath}>
            {getThemeDisplayName(themePath)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

/**
 * Compact Theme Switcher for use in menus or toolbars
 */
export const CompactThemeSwitcher: React.FC = () => {
  return (
    <ThemeSwitcher variant="standard" size="small" fullWidth label="Theme" />
  );
};
